import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QRCode from "qrcode";
import {
  Order,
  Model,
  Workshop,
  Operator,
  Technic,
  Firm,
  OrderImage,
  Currency,
  OrderUnit,
  OrderStatus,
  ModelPriceHistory,
  ModelPriceHistoryItem,
  User,
} from "../types";
import {
  orderService,
  workshopService,
  exchangeRateService,
  modelService,
  userService,
} from "../services/dataService";
import { useAuth } from "../context/AuthContext";
import { getImageUrl } from "../utils/formatters";
import ModelModal from "../components/ModelModal";
import WorkshopModal from "../components/WorkshopModal";
import OperatorModal from "../components/OperatorModal";
import TechnicModal from "../components/TechnicModal";
import CreateTechnicModal from "../components/CreateTechnicModal";
import FirmPickerField from "../components/FirmPicker";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./PurchaseOrders.css";
import "./OrderForm.css";

// Bu tekniklerden biri seçiliyse Desinatör alanı görünür.
const MODELIST_TECHNIC_KEYS = ["digital", "dijital", "sticker", "bsn", "frekans", "lazer", "rubber", "numune"];

const UNIT_OPTIONS: { value: OrderUnit; label: string }[] = [
  { value: OrderUnit.Adet, label: "Adet" },
  { value: OrderUnit.Metre, label: "Metre" },
  { value: OrderUnit.Takim, label: "Takım" },
  { value: OrderUnit.Pano, label: "Pano" },
];

const PRIORITY_OPTIONS = [
  { value: "", label: "Yok" },
  { value: "Düşük", label: "Düşük" },
  { value: "Normal", label: "Normal" },
  { value: "Orta", label: "Orta" },
  { value: "Yüksek", label: "Yüksek" },
];

// "2026-09-30" (yerel gün) ya da ISO tarih → "30 Eyl 2026"
const fmtDay = (s: string) => {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};

const svg = (d: React.ReactNode) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const I = {
  check: svg(<polyline points="20 6 9 17 4 12" />),
  x: svg(<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  plus: svg(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>),
  chev: svg(<polyline points="9 18 15 12 9 6" />),
  user: svg(<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  model: svg(<path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" />),
  image: svg(<><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>),
  trash: svg(<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>),
  download: svg(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>),
  print: svg(<><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>),
};

const OrderForm: React.FC = () => {
  const { orderId } = useParams<{ orderId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!orderId;

  // Geri gitme için history tracking
  const handleGoBack = () => {
    navigate("/"); // Eğer history yoksa ana sayfaya git
  };

  const [loading, setLoading] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(
    null,
  );
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(
    null,
  );
  const [selectedFirm, setSelectedFirm] = useState<Firm | null>(null);
  const [selectedTechnics, setSelectedTechnics] = useState<Technic[]>([]);
  const [selectedModelist, setSelectedModelist] = useState<User | null>(null);
  const [modelists, setModelists] = useState<User[]>([]);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<OrderImage[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [showPriceHistoryModal, setShowPriceHistoryModal] = useState(false);
  const [priceHistory, setPriceHistory] = useState<ModelPriceHistory | null>(
    null,
  );
  const [loadingPriceHistory, setLoadingPriceHistory] = useState(false);

  const [showModelModal, setShowModelModal] = useState(false);
  const [showWorkshopModal, setShowWorkshopModal] = useState(false);
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const [showTechnicModal, setShowTechnicModal] = useState(false);
  const [showCreateTechnicModal, setShowCreateTechnicModal] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState<{
    show: boolean;
    imageUrl: string;
  }>({ show: false, imageUrl: "" });
  const [qrPrintModal, setQrPrintModal] = useState<{
    show: boolean;
    order: Order | null;
    qrCodeDataUrl: string | null;
  }>({ show: false, order: null, qrCodeDataUrl: null });

  // Alert ve Confirm Modal state'leri
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  const [newOrder, setNewOrder] = useState({
    deadline: "",
    quantity: 1,
    unit: OrderUnit.Adet,
    pieceCount: 1,
    width: "",
    height: "",
    price: "",
    currency: "TRY",
    priority: "",
    note: "",
    invoice: "",
    invoiceNumber: "",
  });

  useEffect(() => {
    loadCurrencies();
    loadModelists();
    loadWorkshops();
    if (isEditMode && orderId) {
      loadOrderForEdit(orderId);
    } else {
      // Yeni sipariş için otomatik olarak "Atanmamış İşler" atölyesini seç
      loadDefaultWorkshop();
    }
  }, [isEditMode, orderId]);

  const loadWorkshops = async () => {
    try {
      const workshopsData = await workshopService.getAll();
      setWorkshops(workshopsData);
    } catch (error) {
      console.error("❌ Atölyeler yüklenemedi:", error);
    }
  };

  const loadModelists = async () => {
    try {
      const modelistsData = await userService.getModelists();
      setModelists(modelistsData);
    } catch (error) {
      console.error("❌ Modelistler yüklenemedi:", error);
    }
  };

  const loadDefaultWorkshop = async () => {
    try {
      const workshops = await workshopService.getAll();
      const atanmamisWorkshop = workshops.find(
        (w) =>
          w.name && w.name.toLocaleLowerCase("tr-TR").includes("atanmamış"),
      );
      if (atanmamisWorkshop) {
        setSelectedWorkshop(atanmamisWorkshop);
      }
    } catch (error) {
      console.error("❌ Atölyeler yüklenemedi:", error);
    }
  };

  const loadCurrencies = async () => {
    try {
      const currenciesData = await exchangeRateService.getCurrencies();
      setCurrencies(currenciesData);
    } catch (error) {
      console.error("❌ Failed to load currencies:", error);
    }
  };

  const loadModelCostsAndAutoFill = async (modelId: string) => {
    try {
      setLoadingPriceHistory(true);
      const history = await modelService.getPriceHistory(modelId);
      setPriceHistory(history);

      // Eğer fiyat geçmişi varsa modal aç
      if (history.priceHistory && history.priceHistory.length > 0) {
        setShowPriceHistoryModal(true);
      } else {
      }
    } catch (error) {
      console.error("❌ Fiyat geçmişi yüklenemedi:", error);
    } finally {
      setLoadingPriceHistory(false);
    }
  };

  const handlePriceSelect = (priceItem: ModelPriceHistoryItem) => {
    // Birim tipini OrderUnit enum'una çevir
    let orderUnit = OrderUnit.Adet;
    const unitName = priceItem.orderUnitName.toLocaleLowerCase("tr-TR");
    if (unitName.includes("takım") || unitName.includes("takim")) {
      orderUnit = OrderUnit.Takim;
    } else if (unitName.includes("metre")) {
      orderUnit = OrderUnit.Metre;
    }

    // Fiyat ve birimi otomatik doldur. toFixed(2) DEĞİL: birim fiyat artık
    // 4 ondalık taşıyor (2,375 gibi); burada yuvarlamak, geçmişten seçilen
    // fiyatın kaydedilirken kırpılması ve hassasiyet genişletmesinin bu yazma
    // yolunda geri alınması demekti.
    setNewOrder((prev) => ({
      ...prev,
      price: String(priceItem.price),
      unit: orderUnit,
      currency: priceItem.priceCurrency,
    }));

    setShowPriceHistoryModal(false);
  };

  const loadOrderForEdit = async (id: string) => {
    try {
      setLoading(true);
      // Önce mevcut seçimleri temizle
      setSelectedTechnics([]);

      // Detaylı sipariş bilgisini getById ile al
      const order = await orderService.getById(id);

      if (!order) {
        setAlertModal({
          isOpen: true,
          message: "Sipariş bulunamadı!",
          type: "error",
        });
        handleGoBack();
        return;
      }

      setEditingOrder(order);
      setNewOrder({
        deadline: order.deadline ? order.deadline.split("T")[0] : "",
        quantity: order.quantity || 1,
        unit: order.unit !== undefined ? order.unit : OrderUnit.Adet,
        pieceCount: order.pieceCount || 1,
        width: order.width?.toString() || "",
        height: order.height?.toString() || "",
        price: order.price?.toString() || "",
        currency: order.priceCurrency || order.currency || "TRY",
        priority: order.priority || "",
        note: order.note || "",
        invoice: order.invoice || "",
        invoiceNumber: order.invoiceNumber || "",
      });

      setSelectedFirm(order.firm || null);
      setSelectedModel(order.model || null);
      setSelectedWorkshop(order.workshop || null);
      setSelectedOperator(order.operator || null);

      // Load selected modelist if modelistUserId exists
      if (order.modelistUserId && modelists.length > 0) {
        const modelist = modelists.find(
          (m) => m.userId === order.modelistUserId,
        );
        setSelectedModelist(modelist || null);
      } else {
        setSelectedModelist(null);
      }

      if (order.orderTechnics && order.orderTechnics.length > 0) {
        const technics = order.orderTechnics
          .map((ot) => ot.technic)
          .filter((t): t is Technic => t !== undefined);
        // Duplicate'leri kaldır (technicId'ye göre)
        const uniqueTechnics = technics.filter(
          (technic, index, self) =>
            index === self.findIndex((t) => t.technicId === technic.technicId),
        );
        setSelectedTechnics(uniqueTechnics);
      } else {
        // Sipariş teknik içermiyorsa listeyi temizle
        setSelectedTechnics([]);
      }

      if (order.images && order.images.length > 0) {
        setExistingImages(order.images);
      }
    } catch (error) {
      console.error("❌ Failed to load order:", error);
      setAlertModal({
        isOpen: true,
        message: "Sipariş yüklenemedi!",
        type: "error",
      });
      handleGoBack();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedModel) {
      setAlertModal({
        isOpen: true,
        message: "Lütfen bir model seçin!",
        type: "warning",
      });
      return;
    }

    if (!selectedFirm) {
      setAlertModal({
        isOpen: true,
        message: "Lütfen bir firma seçin!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);

      if (!selectedFirm || !selectedFirm.firmId) {
        throw new Error("Seçili firma bulunamadı");
      }
      if (!selectedModel || !selectedModel.modelId) {
        throw new Error("Seçili model bulunamadı");
      }

      // Atölye seçimini kullan, API kendi mantığı ile atölye ataması yapacak
      const workshopToUse = selectedWorkshop;

      const orderTechnics = selectedTechnics.map((technic) => ({
        orderTechnicId: undefined,
        orderId: undefined,
        technicId: technic.technicId,
        technic: undefined,
      }));

      // Status'u otomatik belirle: "Atanmamış İşler" atölyesi seçilirse veya atölye yoksa Atanmadı, diğer atölyeler için İşlemde
      const orderStatus =
        !workshopToUse ||
        (workshopToUse.name &&
          workshopToUse.name.toLocaleLowerCase("tr-TR").includes("atanmamış"))
          ? OrderStatus.Atanmadi
          : OrderStatus.Islemde;

      const orderData = {
        acceptanceDate: new Date().toISOString(), // Kabul tarihi şu an
        firmId: selectedFirm.firmId,
        modelId: selectedModel.modelId,
        quantity: newOrder.quantity,
        orderUnitId: newOrder.unit, // Backend orderUnitId bekliyor
        pieceCount:
          newOrder.unit === OrderUnit.Takim ? newOrder.pieceCount : undefined,
        width: newOrder.width ? parseFloat(newOrder.width) : undefined,
        height: newOrder.height ? parseFloat(newOrder.height) : undefined,
        price: newOrder.price ? parseFloat(newOrder.price) : undefined,
        priceCurrency: newOrder.currency,
        // workshopId: workshopToUse?.workshopId || undefined, // Backend tekniklere göre atölye ataması yapıyor
        operatorId: selectedOperator?.operatorId || undefined,
        modelistUserId: selectedModelist?.userId || undefined, // Include modelist if selected
        status: orderStatus, // Otomatik belirlenen status
        orderStatusId: orderStatus, // Status ID'yi de gönder
        priority: newOrder.priority || undefined,
        deadline: newOrder.deadline
          ? new Date(newOrder.deadline).toISOString()
          : undefined,
        note: newOrder.note || undefined,
        invoice: newOrder.invoice || undefined,
        invoiceNumber: newOrder.invoiceNumber || undefined,
        createdBy: user?.userId || "unknown-user",
        isActive: true,
        orderTechnics: orderTechnics,
        images: [],
      };
      let resultOrder: Order;

      if (isEditMode && editingOrder) {
        const orderTechnicsForUpdate = selectedTechnics.map((technic) => ({
          orderTechnicId: undefined,
          orderId: editingOrder.orderId,
          technicId: technic.technicId,
          technic: undefined,
        }));

        // Backend'e sadece gerekli alanları gönder, nested objeler olmasın
        // Edit mode'da mevcut statüyü koru - statü değişikliği sadece atölye üzerinden yapılmalı
        const currentStatus = editingOrder.status || OrderStatus.Atanmadi;

        const updateData = {
          orderId: editingOrder.orderId,
          acceptanceDate: editingOrder.acceptanceDate, // Kabul tarihini koru
          completionDate: editingOrder.completionDate,
          deadline: newOrder.deadline
            ? new Date(newOrder.deadline).toISOString()
            : undefined,
          firmId: selectedFirm.firmId,
          modelId: selectedModel.modelId,
          quantity: newOrder.quantity,
          orderUnitId: newOrder.unit,
          pieceCount:
            newOrder.unit === OrderUnit.Takim ? newOrder.pieceCount : undefined,
          width: newOrder.width ? parseFloat(newOrder.width) : undefined,
          height: newOrder.height ? parseFloat(newOrder.height) : undefined,
          price: newOrder.price ? parseFloat(newOrder.price) : undefined,
          priceCurrency: newOrder.currency,
          workshopId: selectedWorkshop?.workshopId || undefined,
          operatorId: selectedOperator?.operatorId || undefined,
          modelistUserId: selectedModelist?.userId || undefined, // Include modelist if selected
          priority: newOrder.priority || undefined,
          note: newOrder.note || undefined,
          invoice: newOrder.invoice || undefined,
          invoiceNumber: newOrder.invoiceNumber || undefined,
          status: currentStatus,
          orderStatusId: currentStatus, // Mevcut statüyü koru
          qrCodeUrl: editingOrder.qrCodeUrl,
          createdAt: editingOrder.createdAt,
          createdBy: editingOrder.createdBy,
          updatedAt: editingOrder.updatedAt,
          updatedBy: editingOrder.updatedBy,
          isActive: editingOrder.isActive,
          orderTechnics: orderTechnicsForUpdate, // Teknikleri de gönder
          images: existingImages, // Mevcut resimleri koru
        };

        await orderService.update(
          editingOrder.orderId,
          updateData as unknown as Order,
        );
        resultOrder = updateData as unknown as Order;
      } else {
        resultOrder = await orderService.create(orderData as any);
        if (resultOrder.qrCodeUrl) {
        } else {
          console.warn("⚠️ QR Code URL not found in response");
        }
      }

      // Resimleri yükle
      if (selectedImages.length > 0 && resultOrder.orderId) {
        try {
          await orderService.uploadImages(resultOrder.orderId, selectedImages);
        } catch (error) {
          console.error("❌ Image upload failed:", error);
          setAlertModal({
            isOpen: true,
            message: "Resimler yüklenemedi, ancak sipariş kaydedildi.",
            type: "warning",
          });
        }
      }

      // Yeni sipariş oluşturulduğunda QR modal göster
      if (!isEditMode && resultOrder) {
        setLoading(false);

        // QR Code URL'den QR kod oluştur
        let qrDataUrl: string | null = null;
        if (resultOrder.qrCodeUrl) {
          try {
            qrDataUrl = await QRCode.toDataURL(resultOrder.qrCodeUrl, {
              width: 400,
              margin: 2,
              color: {
                dark: "#000000",
                light: "#FFFFFF",
              },
              errorCorrectionLevel: "H",
            });
          } catch (qrError) {
            console.error("❌ Failed to generate QR code:", qrError);
          }
        } else {
          console.warn(
            "⚠️ No QR Code URL in response, cannot generate QR code",
          );
        }

        setQrPrintModal({
          show: true,
          order: resultOrder,
          qrCodeDataUrl: qrDataUrl,
        });
        return; // Modal kapatılınca handleGoBack çağrılacak
      }

      setAlertModal({
        isOpen: true,
        message: "Sipariş başarıyla güncellendi!",
        type: "success",
      });
      handleGoBack(); // Önceki sayfaya dön
    } catch (error: any) {
      console.error("❌ Order submission error:", error);

      let errorMessage = `Sipariş ${
        isEditMode ? "güncellenemedi" : "oluşturulamadı"
      }`;

      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        errorMessage =
          "İşlem zaman aşımına uğradı. Sipariş oluşturulmuş olabilir, lütfen sipariş listesini kontrol edin.";
      } else if (error.response?.data) {
        errorMessage += `: ${error.response.data}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      setAlertModal({ isOpen: true, message: errorMessage, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTechnic = (technic: Technic) => {
    setSelectedTechnics((prev) => {
      const isAlreadySelected = prev.some(
        (t) => t.technicId === technic.technicId,
      );

      if (isAlreadySelected) {
        // Zaten seçili ise kaldır
        return prev.filter((t) => t.technicId !== technic.technicId);
      } else {
        // Seçili değilse ekle
        return [...prev, technic];
      }
    });
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedImages([...selectedImages, ...files]);
      const newPreviewUrls = files.map((file) => URL.createObjectURL(file));
      setImagePreviewUrls([...imagePreviewUrls, ...newPreviewUrls]);
    }
  };

  const handleRemoveImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviewUrls = imagePreviewUrls.filter((_, i) => i !== index);
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setSelectedImages(newImages);
    setImagePreviewUrls(newPreviewUrls);
  };

  const handleRemoveExistingImage = async (imageId: string) => {
    setConfirmModal({
      isOpen: true,
      message: "Bu resmi silmek istediğinizden emin misiniz?",
      type: "danger",
      onConfirm: async () => {
        try {
          await orderService.deleteImage(imageId);
          setExistingImages(
            existingImages.filter((img) => img.orderImageId !== imageId),
          );
          setAlertModal({
            isOpen: true,
            message: "Resim başarıyla silindi!",
            type: "success",
          });
        } catch (error) {
          console.error("❌ Failed to delete image:", error);
          setAlertModal({
            isOpen: true,
            message: "Resim silinirken hata oluştu!",
            type: "error",
          });
        }
      },
    });
  };

  // Önizleme ve fiyat geçmişi katmanları Esc ile kapanır (yakalama aşamasında; alttaki dinleyiciler tetiklenmez).
  useEffect(() => {
    if (!imagePreviewModal.show && !showPriceHistoryModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (imagePreviewModal.show) setImagePreviewModal({ show: false, imageUrl: "" });
      else setShowPriceHistoryModal(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [imagePreviewModal.show, showPriceHistoryModal]);

  // Kaydet denendi mi — zorunlu alan uyarıları yalnız bundan sonra alanların altında görünür.
  const [tried, setTried] = useState(false);

  if (loading) {
    return (
      <PageLoader
        message={
          isEditMode ? "Sipariş güncelleniyor..." : "Sipariş oluşturuluyor..."
        }
      />
    );
  }

  const openModelPicker = () => {
    if (!selectedFirm) {
      setAlertModal({
        isOpen: true,
        message: "Lütfen önce bir firma seçiniz!",
        type: "warning",
      });
      return;
    }
    setShowModelModal(true);
  };

  const blockArrows = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
  };

  // Desinatör alanı yalnız bu tekniklerden biri seçiliyse görünür.
  const needsModelist = selectedTechnics.some((t) => {
    const n = t.name.toLocaleLowerCase("tr-TR");
    return MODELIST_TECHNIC_KEYS.some((k) => n.includes(k));
  });

  const isManager = user?.role === "Manager";
  const unitLabel = UNIT_OPTIONS.find((o) => o.value === newOrder.unit)?.label || "";
  const imageCount = existingImages.length + selectedImages.length;
  const priceText = newOrder.price
    ? `${Number(newOrder.price).toLocaleString("tr-TR", { maximumFractionDigits: 4 })} ${newOrder.currency}`
    : "";
  const qtyText =
    Number.isFinite(newOrder.quantity) && newOrder.quantity > 0
      ? `${newOrder.quantity.toLocaleString("tr-TR")} ${unitLabel}`
      : "";
  const submitLabel = isEditMode ? "Değişiklikleri kaydet" : "Siparişi oluştur";
  const closePreview = () => setImagePreviewModal({ show: false, imageUrl: "" });

  return (
    <div className="po-page of-page">
      <header className="po-head">
        <div>
          <span className="po-kicker">Sipariş</span>
          <h1>{isEditMode ? "Siparişi düzenle" : "Yeni sipariş"}</h1>
          <p>
            {isEditMode
              ? `${editingOrder?.acceptanceDate ? `${fmtDay(editingOrder.acceptanceDate)} tarihinde kabul edildi. ` : ""}Durum burada değişmez; atölye üzerinden yönetilir.`
              : "Firma ve modeli seçip miktarı girin; teknik, resim ve notlar isteğe bağlı. Kaydedince QR etiketi hazırlanır."}
          </p>
        </div>
        <div className="po-head-actions of-head-actions">
          <button type="button" className="po-btn" onClick={handleGoBack}>
            Vazgeç
          </button>
          <button type="submit" form="order-form" className="po-btn primary" disabled={loading}>
            {I.check}
            {submitLabel}
          </button>
        </div>
      </header>

      <form
        id="order-form"
        onSubmit={(e) => {
          setTried(true);
          handleSubmitOrder(e);
        }}
        className="of-layout"
        noValidate
      >
        <div className="of-main">
          {/* Müşteri, model, miktar, fiyat */}
          <section className="po-card of-card">
            <div className="po-form-sec">
              <h4>Firma ve model</h4>
              <div className="po-grid2">
                <div className="po-field">
                  <span>
                    Firma <em>zorunlu</em>
                  </span>
                  <FirmPickerField
                    value={selectedFirm?.firmId || null}
                    firm={selectedFirm}
                    onChange={(f) => setSelectedFirm(f)}
                    placeholder="Firma seçmek için tıklayın"
                    className={tried && !selectedFirm ? "of-invalid" : ""}
                  />
                  {tried && !selectedFirm && <span className="po-fielderr">Firma seçin.</span>}
                </div>
                <div className="po-field">
                  <span>
                    Model <em>zorunlu</em>
                  </span>
                  <button
                    type="button"
                    className={`of-pick ${selectedModel ? "has" : ""} ${selectedFirm ? "" : "off"} ${tried && !selectedModel ? "of-invalid" : ""}`}
                    onClick={openModelPicker}
                    aria-haspopup="dialog"
                    aria-disabled={!selectedFirm}
                    title={selectedModel ? selectedModel.modelName : undefined}
                  >
                    <span className="of-pick-mark">{I.model}</span>
                    <span className="of-pick-text">
                      {selectedModel ? (
                        <>
                          <b>{selectedModel.modelName}</b>
                          {selectedModel.modelCode && selectedModel.modelCode !== selectedModel.modelName && (
                            <small>{selectedModel.modelCode}</small>
                          )}
                        </>
                      ) : (
                        <b className="ph">
                          {selectedFirm ? "Model seçmek için tıklayın" : "Önce firma seçin"}
                        </b>
                      )}
                    </span>
                    <span className="po-btn small of-pick-btn">{selectedModel ? "Değiştir" : "Seç"}</span>
                  </button>
                  {tried && !selectedModel ? (
                    <span className="po-fielderr">Model seçin.</span>
                  ) : !selectedFirm ? (
                    <span className="po-fieldnote">Modeller seçilen firmaya göre listelenir.</span>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="po-form-sec">
              <h4>Miktar ve ölçü</h4>
              <div className="po-grid3">
                <label className="po-field">
                  <span>
                    Miktar <em>zorunlu</em>
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    value={newOrder.quantity}
                    onChange={(e) =>
                      setNewOrder({
                        ...newOrder,
                        quantity: parseFloat(e.target.value),
                      })
                    }
                    onKeyDown={blockArrows}
                    required
                  />
                </label>
                <div className={`po-field ${newOrder.unit === OrderUnit.Takim ? "" : "span2"}`}>
                  <span>
                    Birim <em>zorunlu</em>
                  </span>
                  <div className="po-seg of-seg fill" role="radiogroup" aria-label="Birim">
                    {UNIT_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        type="button"
                        role="radio"
                        aria-checked={newOrder.unit === o.value}
                        className={newOrder.unit === o.value ? "on" : ""}
                        onClick={() => setNewOrder({ ...newOrder, unit: o.value })}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                {newOrder.unit === OrderUnit.Takim && (
                  <label className="po-field">
                    <span>
                      Takımda parça sayısı <em>zorunlu</em>
                    </span>
                    <input
                      type="number"
                      min="1"
                      inputMode="numeric"
                      value={newOrder.pieceCount}
                      onChange={(e) =>
                        setNewOrder({
                          ...newOrder,
                          pieceCount: parseInt(e.target.value),
                        })
                      }
                      onKeyDown={blockArrows}
                      placeholder="Bir takımda kaç parça var?"
                      required
                    />
                  </label>
                )}
                <label className="po-field">
                  <span>
                    En <em>isteğe bağlı</em>
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    placeholder="Örn. 10"
                    value={newOrder.width}
                    onChange={(e) => setNewOrder({ ...newOrder, width: e.target.value })}
                    onKeyDown={blockArrows}
                  />
                </label>
                <label className="po-field">
                  <span>
                    Boy <em>isteğe bağlı</em>
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    placeholder="Örn. 20"
                    value={newOrder.height}
                    onChange={(e) => setNewOrder({ ...newOrder, height: e.target.value })}
                    onKeyDown={blockArrows}
                  />
                </label>
                <label className="po-field">
                  <span>Termin tarihi</span>
                  <input
                    type="date"
                    value={newOrder.deadline}
                    onChange={(e) =>
                      setNewOrder({
                        ...newOrder,
                        deadline: e.target.value,
                      })
                    }
                  />
                </label>
              </div>
            </div>

            {isManager && (
              <div className="po-form-sec">
                <h4>Fiyat</h4>
                <div className="po-grid3">
                  <label className="po-field">
                    <span>Birim fiyat</span>
                    <div className="po-money">
                      <input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        placeholder="0,00"
                        value={newOrder.price}
                        onChange={(e) => setNewOrder({ ...newOrder, price: e.target.value })}
                        onKeyDown={blockArrows}
                      />
                      <i>{newOrder.currency}</i>
                    </div>
                  </label>
                  <label className="po-field">
                    <span>Döviz cinsi</span>
                    <select
                      className="po-select"
                      value={newOrder.currency}
                      onChange={(e) => setNewOrder({ ...newOrder, currency: e.target.value })}
                    >
                      {currencies.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!isEditMode && (
                    <p className="po-fieldnote of-price-note">
                      Model seçildiğinde bu modelin önceki sipariş fiyatları önerilir; birini seçince fiyat, birim ve döviz dolar.
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Teknikler */}
          <section className="po-card of-card">
            <div className="po-form-sec">
              <h4>
                Teknikler
                {selectedTechnics.length > 0 && <em>{selectedTechnics.length}</em>}
              </h4>

              {isEditMode && editingOrder?.modelistUser && (
                <div className="po-note info of-note">
                  {I.user}
                  <span>
                    Desinatör atanmış:{" "}
                    <b>
                      {editingOrder.modelistUser.firstName} {editingOrder.modelistUser.lastName}
                    </b>
                  </span>
                </div>
              )}

              {selectedTechnics.length > 0 ? (
                <div className="of-chips">
                  {selectedTechnics.map((technic) => (
                    <span key={technic.technicId} className="of-chip">
                      {technic.name}
                      <button
                        type="button"
                        aria-label={`${technic.name} tekniğini kaldır`}
                        title="Kaldır"
                        onClick={() =>
                          setSelectedTechnics(
                            selectedTechnics.filter((t) => t.technicId !== technic.technicId),
                          )
                        }
                      >
                        {I.x}
                      </button>
                    </span>
                  ))}
                  <span className="of-chip-acts">
                    <button type="button" className="po-btn small" onClick={() => setShowTechnicModal(true)}>
                      {I.plus}
                      Ekle / çıkar
                    </button>
                    <button type="button" className="po-btn small" onClick={() => setSelectedTechnics([])}>
                      Temizle
                    </button>
                  </span>
                </div>
              ) : (
                <button type="button" className="of-empty" onClick={() => setShowTechnicModal(true)}>
                  <b>Henüz teknik seçilmedi</b>
                  <span>Baskı, nakış vb. teknikleri seçmek için tıklayın.</span>
                </button>
              )}

              {needsModelist && (
                <div className="po-grid2">
                  <label className="po-field">
                    <span>Desinatör</span>
                    <select
                      value={selectedModelist?.userId || ""}
                      onChange={(e) => {
                        const modelist = modelists.find((m) => m.userId === e.target.value);
                        setSelectedModelist(modelist || null);
                      }}
                    >
                      <option value="">Desinatör seçin</option>
                      {modelists.map((modelist) => (
                        <option key={modelist.userId} value={modelist.userId}>
                          {modelist.firstName} {modelist.lastName}
                        </option>
                      ))}
                    </select>
                    <span className="po-fieldnote">Seçilen teknik desen hazırlığı gerektiriyor.</span>
                  </label>
                </div>
              )}
            </div>
          </section>

          {/* Resimler */}
          <section className="po-card of-card">
            <div className="po-form-sec">
              <h4>
                Resimler
                {imageCount > 0 && <em>{imageCount}</em>}
              </h4>

              {isEditMode && existingImages.length > 0 && (
                <>
                  <span className="of-sublabel">Mevcut resimler</span>
                  <div className="of-thumbs">
                    {existingImages.map((image) => {
                      // API base URL'den image URL'i oluştur
                      const imageUrl = getImageUrl(image.imageUrl);
                      return (
                        <div key={image.orderImageId} className="of-thumb">
                          <button
                            type="button"
                            className="of-thumb-img"
                            onClick={() => setImagePreviewModal({ show: true, imageUrl })}
                            aria-label="Resmi büyüt"
                          >
                            <img
                              src={imageUrl}
                              alt={image.description || "Sipariş resmi"}
                              onError={(e) => {
                                console.error("Image load error:", imageUrl);
                                (e.target as HTMLImageElement).src =
                                  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23ddd" width="200" height="200"/><text fill="%23999" x="50%" y="50%" text-anchor="middle" dy=".3em">Resim yüklenemedi</text></svg>';
                              }}
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveExistingImage(image.orderImageId)}
                            className="po-ib danger of-thumb-x"
                            title="Resmi sil"
                            aria-label="Resmi sil"
                          >
                            {I.trash}
                          </button>
                          {image.description && <span className="of-thumb-cap">{image.description}</span>}
                        </div>
                      );
                    })}
                  </div>
                  <span className="of-sublabel">Yeni resim ekle</span>
                </>
              )}

              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="of-file"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="of-drop">
                <span className="of-drop-icon">{I.image}</span>
                <span className="of-drop-text">
                  <b>Resim seç</b>
                  <small>Birden fazla resim seçebilirsiniz.</small>
                </span>
              </label>

              {imagePreviewUrls.length > 0 && (
                <div className="of-thumbs">
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} className="of-thumb">
                      <button
                        type="button"
                        className="of-thumb-img"
                        onClick={() => setImagePreviewModal({ show: true, imageUrl: url })}
                        aria-label="Resmi büyüt"
                      >
                        <img src={url} alt={`Önizleme ${index + 1}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="po-ib danger of-thumb-x"
                        title="Kaldır"
                        aria-label="Resmi kaldır"
                      >
                        {I.x}
                      </button>
                      <span className="of-thumb-cap new">Yeni</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Öncelik ve notlar */}
          <section className="po-card of-card">
            <div className="po-form-sec">
              <h4>Öncelik ve notlar</h4>
              <div className="po-field">
                <span>Öncelik</span>
                <div className="po-seg of-seg" role="radiogroup" aria-label="Öncelik">
                  {PRIORITY_OPTIONS.map((o) => (
                    <button
                      key={o.value || "none"}
                      type="button"
                      role="radio"
                      aria-checked={newOrder.priority === o.value}
                      className={newOrder.priority === o.value ? "on" : ""}
                      onClick={() => setNewOrder({ ...newOrder, priority: o.value })}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="po-field">
                <span>Notlar</span>
                <textarea
                  value={newOrder.note}
                  onChange={(e) => setNewOrder({ ...newOrder, note: e.target.value })}
                  rows={3}
                  placeholder="Sipariş ile ilgili notlar..."
                />
              </label>
            </div>
          </section>
        </div>

        {/* Özet + eylemler: genişte yan kart (yapışkan), darda alt eylem çubuğu */}
        <aside className="of-side">
          <div className="po-card of-sum">
            <div className="of-sum-head">
              <span className="po-kicker">Özet</span>
              {isEditMode ? (
                <span className="po-badge info">Düzenleme</span>
              ) : (
                <span className="po-badge muted">Yeni</span>
              )}
            </div>
            <dl className="of-sum-list">
              <div>
                <dt>Firma</dt>
                <dd className={selectedFirm ? "" : "none"}>{selectedFirm?.firmName || "Seçilmedi"}</dd>
              </div>
              <div>
                <dt>Model</dt>
                <dd className={selectedModel ? "" : "none"}>{selectedModel?.modelName || "Seçilmedi"}</dd>
              </div>
              <div>
                <dt>Miktar</dt>
                <dd className={qtyText ? "" : "none"}>
                  {qtyText || "—"}
                  {newOrder.unit === OrderUnit.Takim && Number.isFinite(newOrder.pieceCount) && (
                    <small>takımda {newOrder.pieceCount} parça</small>
                  )}
                </dd>
              </div>
              {(newOrder.width || newOrder.height) && (
                <div>
                  <dt>En × boy</dt>
                  <dd>
                    {newOrder.width || "—"} × {newOrder.height || "—"}
                  </dd>
                </div>
              )}
              <div>
                <dt>Termin</dt>
                <dd className={newOrder.deadline ? "" : "none"}>
                  {newOrder.deadline ? fmtDay(newOrder.deadline) : "Belirtilmedi"}
                </dd>
              </div>
              {isManager && (
                <div>
                  <dt>Birim fiyat</dt>
                  <dd className={priceText ? "" : "none"}>{priceText ? `${priceText} / ${unitLabel}` : "Girilmedi"}</dd>
                </div>
              )}
              <div>
                <dt>Teknik</dt>
                <dd className={selectedTechnics.length ? "" : "none"}>
                  {selectedTechnics.length ? selectedTechnics.map((t) => t.name).join(", ") : "Yok"}
                </dd>
              </div>
              {(newOrder.priority || imageCount > 0) && (
                <div>
                  <dt>Diğer</dt>
                  <dd>
                    {[newOrder.priority && `${newOrder.priority} öncelik`, imageCount > 0 && `${imageCount} resim`]
                      .filter(Boolean)
                      .join(" · ")}
                  </dd>
                </div>
              )}
            </dl>
            <div className="of-sum-line" aria-hidden="true">
              <b>{selectedModel?.modelName || "Model seçilmedi"}</b>
              <small>{[selectedFirm?.firmName, qtyText].filter(Boolean).join(" · ") || "Firma seçilmedi"}</small>
            </div>
            <div className="of-side-acts">
              <button type="button" className="po-btn" onClick={handleGoBack}>
                Vazgeç
              </button>
              <button type="submit" className="po-btn primary" disabled={loading}>
                {I.check}
                {submitLabel}
              </button>
            </div>
          </div>
        </aside>
      </form>

      {/* Modals */}
      <ModelModal
        isOpen={showModelModal}
        onClose={() => setShowModelModal(false)}
        onSelect={(model) => {
          setSelectedModel(model);
          if (model && !isEditMode) {
            loadModelCostsAndAutoFill(model.modelId);
          }
        }}
        firmId={selectedFirm?.firmId}
        selectedFirm={selectedFirm}
      />
      <WorkshopModal
        isOpen={showWorkshopModal}
        onClose={() => setShowWorkshopModal(false)}
        onSelect={(workshop) => setSelectedWorkshop(workshop)}
      />
      <OperatorModal
        isOpen={showOperatorModal}
        onClose={() => setShowOperatorModal(false)}
        onSelect={(operator) => setSelectedOperator(operator)}
        workshopId={selectedWorkshop?.workshopId}
      />
      <TechnicModal
        isOpen={showTechnicModal}
        onClose={() => setShowTechnicModal(false)}
        onSelectTechnic={handleSelectTechnic}
        selectedTechnics={selectedTechnics}
        onClearAll={() => setSelectedTechnics([])}
        workshops={workshops}
        onOpenCreateModal={() => {
          setShowTechnicModal(false);
          setShowCreateTechnicModal(true);
        }}
      />
      <CreateTechnicModal
        isOpen={showCreateTechnicModal}
        onClose={() => {
          setShowCreateTechnicModal(false);
          setShowTechnicModal(true);
        }}
        onTechnicCreated={(newTechnic) => {
          // Yeni tekniği seçili tekniklere ekle ve teknik modalına dön
          handleSelectTechnic(newTechnic);
          setShowCreateTechnicModal(false);
          setShowTechnicModal(true);
        }}
        workshops={workshops}
      />

      {/* Resim önizleme */}
      {imagePreviewModal.show && (
        <div className="po-overlay center of-overlay of-viewer" onClick={closePreview} role="dialog" aria-modal="true" aria-label="Resim önizleme">
          <div className="of-viewer-bar" onClick={(e) => e.stopPropagation()}>
            <a
              href={imagePreviewModal.imageUrl}
              download={`order-image-${Date.now()}.jpg`}
              className="po-btn small"
              title="Resmi indir"
            >
              {I.download}
              İndir
            </a>
            <button type="button" className="po-btn small" onClick={closePreview} title="Kapat" aria-label="Kapat">
              {I.x}
              Kapat
            </button>
          </div>
          <img src={imagePreviewModal.imageUrl} alt="Büyük önizleme" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* QR etiketi (yeni sipariş kaydedildikten sonra) */}
      {qrPrintModal.show && qrPrintModal.order && (
        <div className="po-overlay center of-overlay" role="dialog" aria-modal="true" aria-labelledby="of-qr-title">
          <div className="po-modal of-qr">
            <div className="po-modal-head">
              <div>
                <span className="po-kicker">Sipariş</span>
                <h3 id="of-qr-title">Sipariş oluşturuldu</h3>
              </div>
              <span className="po-badge ok">{I.check} Kaydedildi</span>
            </div>
            <div className="po-modal-body">
              <dl className="po-facts">
                <div>
                  <dt>Firma</dt>
                  <dd>{qrPrintModal.order.firm?.firmName || selectedFirm?.firmName || "-"}</dd>
                </div>
                <div>
                  <dt>Model</dt>
                  <dd>{qrPrintModal.order.model?.modelName || selectedModel?.modelName || "-"}</dd>
                </div>
                <div>
                  <dt>Miktar</dt>
                  <dd>{qrPrintModal.order.quantity}</dd>
                </div>
                <div>
                  <dt>Atölye</dt>
                  <dd>{qrPrintModal.order.workshop?.name || selectedWorkshop?.name || "-"}</dd>
                </div>
                {qrPrintModal.order.note && (
                  <div className="wide">
                    <dt>Not</dt>
                    <dd>{qrPrintModal.order.note}</dd>
                  </div>
                )}
                <div className="wide">
                  <dt>Sipariş no</dt>
                  <dd className="of-mono">{qrPrintModal.order.orderId}</dd>
                </div>
              </dl>

              {qrPrintModal.qrCodeDataUrl ? (
                <div className="of-qr-box">
                  <img src={qrPrintModal.qrCodeDataUrl} alt="Sipariş QR kodu" />
                  <div>
                    <b>QR kod hazır</b>
                    <small>Yazdırıp ürüne/iş föyüne yapıştırın; okutunca sipariş açılır.</small>
                    <small className="of-mono">{qrPrintModal.order.qrCodeUrl}</small>
                  </div>
                </div>
              ) : (
                <div className="po-note warn">
                  <b>QR kod oluşturulamadı.</b>{" "}
                  {qrPrintModal.order.qrCodeUrl
                    ? `URL alındı ama QR kod üretilemedi: ${qrPrintModal.order.qrCodeUrl}`
                    : "Sunucudan QR adresi alınamadı."}
                </div>
              )}
            </div>
            <div className="po-modal-foot between of-qr-foot">
              {qrPrintModal.qrCodeDataUrl ? (
                <div className="po-foot-btns">
                  <button
                    type="button"
                    className="po-btn"
                    onClick={() => {
                      const printWindow = window.open("", "_blank");
                      if (
                        printWindow &&
                        qrPrintModal.qrCodeDataUrl &&
                        qrPrintModal.order
                      ) {
                        printWindow.document.write(`
                          <html>
                            <head>
                              <title>QR Kod - ${
                                qrPrintModal.order.orderId
                              }</title>
                              <style>
                                body {
                                  display: flex;
                                  flex-direction: column;
                                  align-items: center;
                                  justify-content: center;
                                  min-height: 100vh;
                                  margin: 0;
                                  font-family: Arial, sans-serif;
                                }
                                .qr-container {
                                  text-align: center;
                                  padding: 1.25rem;
                                }
                                img {
                                  width: 15.625rem;
                                  height: 15.625rem;
                                  margin: 1.25rem 0;
                                }
                                .info {
                                  margin: 0.625rem 0;
                                  font-size: 1.875rem;
                                }
                                h2 {
                                  font-size: 1.875rem;
                                }
                                @media print {
                                  body { margin: 0; }
                                }
                              </style>
                            </head>
                            <body>
                              <div class="qr-container">
                                <h2>Sipariş QR Kodu</h2>
                                <img src="${
                                  qrPrintModal.qrCodeDataUrl
                                }" alt="QR Code" />
                                <div class="info"><strong>Sipariş No:</strong> ${
                                  qrPrintModal.order.orderId
                                }</div>
                                <div class="info"><strong>Firma:</strong> ${
                                  qrPrintModal.order.firm?.firmName ||
                                  selectedFirm?.firmName ||
                                  "-"
                                }</div>
                                <div class="info"><strong>Model:</strong> ${
                                  qrPrintModal.order.model?.modelName ||
                                  selectedModel?.modelName ||
                                  "-"
                                }</div>
                                <div class="info"><strong>Miktar:</strong> ${
                                  qrPrintModal.order.quantity
                                }</div>
                                ${
                                  qrPrintModal.order.note
                                    ? `<div class="info"><strong>Not:</strong> ${qrPrintModal.order.note}</div>`
                                    : ""
                                }
                              </div>
                            </body>
                          </html>
                        `);
                        printWindow.document.close();
                        setTimeout(() => {
                          printWindow.print();
                        }, 250);
                      }
                    }}
                  >
                    {I.print}
                    Yazdır
                  </button>
                  <a
                    href={qrPrintModal.qrCodeDataUrl || ""}
                    download={`order-${qrPrintModal.order.orderId}-qr.png`}
                    className="po-btn"
                  >
                    {I.download}
                    İndir
                  </a>
                </div>
              ) : (
                <span />
              )}
              <button
                type="button"
                className="po-btn primary"
                onClick={() => {
                  setQrPrintModal({
                    show: false,
                    order: null,
                    qrCodeDataUrl: null,
                  });
                  handleGoBack();
                }}
              >
                Kapat ve siparişlere dön
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fiyat geçmişi (yeni siparişte model seçilince) */}
      {showPriceHistoryModal && priceHistory && (
        <div
          className="po-overlay center of-overlay"
          onClick={() => setShowPriceHistoryModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="of-ph-title"
        >
          <div className="po-modal of-ph" onClick={(e) => e.stopPropagation()}>
            <div className="po-modal-head">
              <div>
                <span className="po-kicker">Önceki fiyatlar</span>
                <h3 id="of-ph-title">{priceHistory.modelName}</h3>
                <p className="po-form-hint">Daha önce verilen fiyatlardan birini seçin; fiyat, birim ve döviz forma dolar.</p>
              </div>
              <button
                type="button"
                className="po-ib big"
                aria-label="Kapat"
                onClick={() => setShowPriceHistoryModal(false)}
              >
                {I.x}
              </button>
            </div>
            <div className="po-modal-body">
              {loadingPriceHistory ? (
                <div className="po-loading">
                  <span className="po-spin" />
                  Yükleniyor...
                </div>
              ) : (
                <div className="of-ph-list">
                  {priceHistory.priceHistory.map((item) => (
                    <button
                      key={item.orderId}
                      type="button"
                      className="of-ph-item"
                      onClick={() => handlePriceSelect(item)}
                    >
                      <span className="of-ph-main">
                        <b>
                          {new Intl.NumberFormat("tr-TR", {
                            style: "currency",
                            currency: item.priceCurrency,
                          }).format(item.price)}
                        </b>
                        <small>
                          {item.firmName} · {item.quantity} {item.orderUnitName}
                        </small>
                      </span>
                      <span className="of-ph-date">
                        {new Date(item.acceptanceDate).toLocaleDateString("tr-TR")}
                      </span>
                      <span className="of-ph-go">{I.chev}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="po-modal-foot">
              <button type="button" className="po-btn" onClick={() => setShowPriceHistoryModal(false)}>
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

export default OrderForm;
